import React from "react";

import { times } from "lodash/fp";

import {
  Box,
  Divider,
  Stack,
  TableBody as MuiTableBody,
  TableBodyProps as MuiTableBodyProps,
  styled,
} from "@mui/material";

import NothingToReport from "@app/analytics/components/NothingToReport";

import TableCell from "./TableCell";
import TableRow from "./TableRow";

const DEFAULT_NUM_EMPTY_ROWS = 5;

interface TableBodyProps extends MuiTableBodyProps {
  loading?: boolean;
  empty?: boolean;
  emptyMessage?: {
    title?: string;
    subtitle?: string;
  };
  numRows?: number;
}

const StyledTableBody = styled(MuiTableBody)({
  backgroundColor: "#FFF",
  height: "314px",
  position: "relative",
  "&.MuiTableBody-root .MuiTableCell-root": {
    height: "314px",
    position: "relative",
  },
});

const TableBody = ({
  children,
  loading,
  empty,
  emptyMessage,
  numRows = DEFAULT_NUM_EMPTY_ROWS,
  sx,
}: TableBodyProps) => {
  if (loading || empty) {
    return (
      <StyledTableBody sx={sx}>
        <TableRow>
          <TableCell
            colSpan={100} // large number just to ensure this cell spans all columns
          >
            <Stack
              sx={{
                position: "absolute",
                p: 2,
                width: "100%",
                height: "100%",
                top: 0,
                left: 0,
              }}
            >
              {times(
                (i) => (
                  <Stack key={i}>
                    {i > 0 && <Divider sx={{ my: 2 }} />}
                    <Box
                      sx={{
                        bgcolor: "shades.neutral.50",
                        borderRadius: "8px",
                        height: "30px",
                      }}
                    />
                  </Stack>
                ),
                loading ? numRows : DEFAULT_NUM_EMPTY_ROWS
              )}
              {!loading && empty && (
                <NothingToReport
                  title={emptyMessage?.title}
                  subtitle={emptyMessage?.subtitle}
                  sx={{
                    position: "absolute",
                    width: "calc(100% - 16px)",
                    height: "calc(100% - 16px)",
                    background:
                      "linear-gradient(0deg, #FFFFFF 56.95%, rgba(255, 255, 255, 0) 100%)",
                  }}
                />
              )}
            </Stack>
          </TableCell>
        </TableRow>
      </StyledTableBody>
    );
  }

  return <MuiTableBody sx={sx}>{children}</MuiTableBody>;
};

export default TableBody;
